import type { PageTransition } from '@matter_extractor/shared';
import { ANIMATION_SPEED_MS } from '@matter_extractor/shared';
import type { ReaderMotion } from './reader-theme';

/**
 * The transition between two pages (§9.3), as a class and a duration.
 *
 * The reader plays ONE page: chaining two is the caller's business - the
 * session advances, the Timeline shows a scene - so what crosses this seam is
 * the couple « classe + durée » rather than a machine that owns both pages.
 * `PageTransitionFrame` is the small component built on it, for a caller that
 * only ever shows the arriving page.
 *
 * Three animations and not eight (§9.3): a transition moves a whole page, and
 * the eight of a block are an entry.
 *
 * The transition is carried by the edge LEAVING a page, so the one to play
 * between page N and page N+1 is `pages[N].transition` - the last page's is
 * never played.
 */
export type PageTransitionCss = {
  /** On the arriving page. Empty when nothing is played. */
  readonly enterClassName: string;
  /** On the leaving page, for a caller that keeps both mounted. Empty likewise. */
  readonly leaveClassName: string;
  /** What to wait before the leaving page may be unmounted. 0 when nothing is played. */
  readonly durationMs: number;
  /** The property the two classes spend. Hand it to the element that carries them. */
  readonly style: Record<string, string>;
};

const NONE: PageTransitionCss = {
  enterClassName: '',
  leaveClassName: '',
  durationMs: 0,
  style: {},
};

/**
 * Reduced motion cancels a page transition as it cancels a block's entry
 * (§9.4): what it does not cancel is a delay, and a transition is not one.
 */
export function pageTransitionCss(
  transition: PageTransition,
  motion: ReaderMotion,
): PageTransitionCss {
  if (transition.animation === 'none' || motion === 'reduced') return NONE;
  const durationMs = ANIMATION_SPEED_MS[transition.speed];
  return {
    enterClassName: `lecteur-page-entre lecteur-page-entre-${transition.animation}`,
    leaveClassName: `lecteur-page-sort lecteur-page-sort-${transition.animation}`,
    durationMs,
    style: { '--lecteur-transition-ms': `${durationMs}ms` },
  };
}
